<!-- Native <dialog>: the top layer sits above every stacking context, so this needs no
     z-index and gets its focus trap and Escape handling from the browser. -->
<dialog
  #dialog
  class="w-full max-w-3xl rounded-card bg-surface-card p-0 text-fg backdrop:bg-black/60"
  (close)="close()"
>
  @if (spinClass$ | async; as detail) {
    <div
      class="flex items-center justify-between border-b border-border/60 px-4 py-4 sm:px-6"
    >
      <div>
        <div class="flex items-center gap-2">
          <h2 class="text-lg font-semibold text-fg">
            {{ detail.dateEpoch | date: 'EEEE d MMMM' | titlecase }}
          </h2>
          @if (detail.currentReservations >= detail.maxReservations) {
            <app-badge tone="danger">Volzet</app-badge>
          }
        </div>
        <p class="text-sm text-fg-muted">
          om {{ detail.dateEpoch | date: 'HH:mm' }}, {{ detail.type }}
        </p>
      </div>
      <button
        type="button"
        class="rounded-md p-2 text-fg-muted hover:bg-surface-raised hover:text-fg"
        aria-label="Sluiten"
        (click)="close()"
      >
        <app-icon name="x" size="size-5" />
      </button>
    </div>

    <div class="space-y-5 px-4 py-5 sm:p-6">
      <div class="flex flex-wrap items-center gap-4">
        <div class="flex-1">
          <div
            class="h-2 overflow-hidden rounded-full bg-surface-raised"
            role="progressbar"
            [attr.aria-valuenow]="
              (detail.currentReservations / detail.maxReservations) * 100
            "
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              class="h-full bg-brand"
              [style.width.%]="
                (detail.currentReservations / detail.maxReservations) * 100
              "
            ></div>
          </div>
          <!-- Counts what is FREE, like every other screen. This said "bezet", so the
               same class read 12 here and 8 on the booking list, one click apart. -->
          <p class="mt-1 text-sm text-fg-muted">
            {{ detail.maxReservations - detail.currentReservations }} van
            {{ detail.maxReservations }} fietsen vrij
          </p>
        </div>

        <button
          appButton
          size="sm"
          type="button"
          [disabled]="
            inSubmission ||
            detail.currentReservations + 1 > detail.maxReservations
          "
          (click)="addAdminReservation()"
        >
          <app-icon name="plus" size="size-4" />
          Fiets voor mezelf
        </button>

        <!-- Secondary: deleting the class is not what an admin opens this dialog to do,
             and the confirm step is where the red belongs. -->
        <button
          appButton
          variant="secondary"
          size="sm"
          type="button"
          [disabled]="inSubmission"
          (click)="removeClass()"
        >
          <app-icon name="trash" size="size-4" />
          Les verwijderen
        </button>
      </div>

      <!-- Type and capacity change in place, so a wrong choice at creation does not cost
           the bookings a delete-and-recreate would. Folded shut: most visits to this
           dialog are to see who booked. -->
      <details class="group rounded-md bg-surface-raised/60 px-4 py-2">
        <summary
          class="flex min-h-11 cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold text-brand marker:content-none"
        >
          Les aanpassen
          <app-icon
            name="chevron-down"
            size="size-4"
            class="transition-transform group-open:rotate-180"
          />
        </summary>
        <form
          [formGroup]="editForm"
          (ngSubmit)="saveEdit()"
          class="grid gap-4 pb-3 pt-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
        >
          <app-field label="Type" for="editType" [control]="editType">
            <select
              appInput
              [control]="editType"
              formControlName="type"
              id="editType"
            >
              @for (classType of classTypes; track classType) {
                <option [value]="classType">{{ classType }}</option>
              }
            </select>
          </app-field>
          <app-field
            label="Aantal fietsen"
            for="editMax"
            [hint]="'Al gereserveerd: ' + detail.currentReservations + '.'"
            [control]="editMax"
          >
            <input
              appInput
              [control]="editMax"
              formControlName="maxReservations"
              id="editMax"
              type="number"
              inputmode="numeric"
              [attr.min]="detail.currentReservations || 1"
              max="200"
            />
          </app-field>
          <button
            appButton
            size="sm"
            type="submit"
            class="min-h-11"
            [disabled]="editForm.invalid || inSubmission"
          >
            Opslaan
          </button>
        </form>
      </details>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-sm">
          <thead
            class="text-xs font-semibold uppercase tracking-wide text-fg-subtle"
          >
            <tr>
              <th scope="col" class="py-2 pr-3">Naam</th>
              <th scope="col" class="hidden py-2 pr-3 sm:table-cell">E-mail</th>
              <th scope="col" class="py-2 pr-3 text-right">Fietsen</th>
              <th scope="col" class="py-2 text-right">Aanpassen</th>
            </tr>
          </thead>
          <!-- The rows live under an @if on the resolved list, not straight on the async
               pipe: before the first emission the pipe yields null, @for over null runs
               its @empty branch, and the dialog stated "nog geen reservaties" while the
               attendees were still loading — on the screen where an admin decides
               whether to delete the class. -->
          <tbody class="divide-y divide-border/60">
            @if (users$ | async; as users) {
              @for (user of users; track user.id) {
                <tr>
                  <td class="py-3 pr-3">
                    <span class="text-fg">
                      {{ user.firstName | titlecase }}
                      {{ user.lastName | titlecase }}
                    </span>
                    @if (user.trial) {
                      <app-badge tone="warning" class="ml-2"
                        >Proefles</app-badge
                      >
                    }
                  </td>
                  <td class="hidden py-3 pr-3 text-fg-muted sm:table-cell">
                    {{ user.email }}
                  </td>
                  <td class="py-3 pr-3 text-right font-semibold text-fg">
                    {{ user.reservationsForCurrentClass }}
                  </td>
                  <td class="py-3 text-right">
                    <div class="inline-flex gap-2">
                      <button
                        appButton
                        variant="secondary"
                        size="sm"
                        type="button"
                        aria-label="Fiets toevoegen"
                        [disabled]="
                          inSubmission ||
                          detail.currentReservations + 1 >
                            detail.maxReservations
                        "
                        (click)="addReservation(user.id)"
                      >
                        <app-icon name="plus" size="size-4" />
                      </button>
                      <button
                        appButton
                        variant="secondary"
                        size="sm"
                        type="button"
                        aria-label="Fiets verwijderen"
                        [disabled]="
                          inSubmission || user.reservationsForCurrentClass < 1
                        "
                        (click)="removeReservation(user.id)"
                      >
                        <app-icon name="minus" size="size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="4" class="py-8 text-center text-fg-muted">
                    Nog geen reservaties voor deze les.
                  </td>
                </tr>
              }
            } @else {
              <tr>
                <td colspan="4" class="py-8 text-center">
                  <app-spinner />
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</dialog>
